import '../../platform.css';
import './teamView.css';

import { useRecoilValue } from 'recoil';
import { useEffect, useMemo, useRef, useState } from 'react';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { userSettingsState } from '../../recoil/atoms/userSettingsState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { playersState } from '../../recoil/atoms/playersState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { teamsState } from '../../recoil/atoms/teamsState';
import { playerTeamDataSelector } from '../../recoil/selectors/playerTeamDataSelector';
import { useAuthContext } from '../../../common/contexts/useAuthContext';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MenuIcon from '@mui/icons-material/Menu';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { translate } from '../../../common/language/translations';
import { FootballFieldSvg } from '../../../assets/svg/FootballFieldSvg';
import { getEmptyPlayerSimpleTableWidth, getPlayerSimpleTableRowHeight } from '../tables/playerSimpleTable/playerSimpleColumns';

import { RoleConfigTable, roleConfigTableRowHeight, roleConfigTableWidth } from '../tables/playerSimpleTable/RoleConfigTable';
import { trackEvent } from '../../services/server/analytics/trackEvent';
import { PlayerSimpleTable, PlayerSimpleTableType } from '../tables/playerSimpleTable/PlayerSimpleTable';
import { removePlayerFromTeam } from '../../services/firestore/teams';
import { removePlayerFromAcademy, removePlayerFromOwnTeam } from '../../services/firestore/players';
import { Team, ArchivedTeam, RoleConfigTeam} from '../../types/team';
import { StringToAnyMap } from '../../types/common';
import { PlatformPosition, PlatformPositionKey } from '../../types/position';
import { PlayerOverviews, PlayerId, PlayerOverview, PlayerEntry } from '../../types/player';
import { AggregationRule } from '../../types/tenant';
import { getStrictPlayerId } from '../../utils/playerUtils';
import { getAge, getAgeCategory } from '../../utils/ageUtils';
import { getContractCategory, getMonthsLeftOfContract } from '../../utils/contractUtils';
import { isValidPlayerLocality, isValidPlayerRole } from '../../utils/playerUtils';
import { playerRoleToSortOrder } from '../../static/constants/playerRoles';
import { playerLocalityToSortOrder } from '../../static/constants/playerLocalities';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { roleConfigIsBeingComputed } from '../../utils/metricUtils';
import { defaultFormationConfigMap } from '../../static/constants/formations';
import { getGridPosition } from '../../utils/positionUtils';
import { getAlignedTeam, resolveTableCollisions } from '../../utils/teamViewUtils';
import { useDebouncedWindowSize } from '../../../common/hooks/WindowSize';
import { formationConfigHasTypicalBackline } from '../../utils/formationUtils';


export interface InitialTeamDataPoint {
  position: PlatformPosition;
  left: number;
  top: number;
  xCoordinate: number;
  yCoordinate: number;
  playerList: StringToAnyMap[];
}


export interface TeamDataPoint extends InitialTeamDataPoint {
  height: number;
  desiredHeight: number;
  width: number;
  lowestSafeMarginXToNodes: number;
}


interface TeamViewProps {
  teamId: string | 'ownTeamPlanner' | 'archivedTeam' | 'roleConfigs' | undefined;
  team?: Team | ArchivedTeam | RoleConfigTeam; // team | archivedTeam | roleConfigs
  hasOnlyReadAccess?: boolean;

  draggedPlayer?: StringToAnyMap | undefined;
  setDraggedPlayer?: (player: StringToAnyMap | undefined) => void;
  handlePlayerDrop?: (newPosition: PlatformPositionKey, positionIndex?: number) => void;
  droppedPlayer?: StringToAnyMap | undefined;
  setDroppedPlayer?: (player: StringToAnyMap | undefined) => void;
  handleOpenAddClauseModal?: (playerId: number, wasAdded: boolean) => void;
  handlePlayerClick?: (player: StringToAnyMap) => void;

  isLeftSectionExpanded?: boolean;
  setIsLeftSectionExpanded?: (isExpanded: boolean) => void;
  rightSectionExpansionType?: string | undefined;
  setRightSectionExpansionType?: (expansionType: string | undefined) => void;

  showFirstEleven?: boolean;
  benchPlayers?: StringToAnyMap[];
  tableHoverDisabled?: boolean;

  isArchivedTeamDeleted?: boolean;
  closeArchivedTeam?: () => void;
  deleteOrRestoreArchivedTeam?: () => void;
  additionalPlayerOverviews?: PlayerOverviews;

  highlightedPlayerId?: PlayerId | undefined;
  confirmNewAggregationRule?: (newAggregationRule: AggregationRule, position: PlatformPosition) => void;

  tableTransition?: number;
  isSmallTeamView?: boolean;
  defaultOrderBy?: string;
  defaultToShortNames?: boolean;
  currentTeamPlayerMap?: StringToAnyMap;
  selectedDate?: Date;
}


export const TeamView: React.FC<TeamViewProps> = ({
  teamId,
  team,
  hasOnlyReadAccess,

  draggedPlayer,
  setDraggedPlayer,
  handlePlayerDrop,
  droppedPlayer,
  setDroppedPlayer,
  handleOpenAddClauseModal,
  handlePlayerClick,

  isLeftSectionExpanded,
  setIsLeftSectionExpanded,
  rightSectionExpansionType,
  setRightSectionExpansionType,

  showFirstEleven,
  benchPlayers,
  tableHoverDisabled,

  isArchivedTeamDeleted,
  closeArchivedTeam,
  deleteOrRestoreArchivedTeam,
  additionalPlayerOverviews,

  highlightedPlayerId,
  confirmNewAggregationRule,

  tableTransition,
  isSmallTeamView,
  defaultOrderBy,
  defaultToShortNames,
  currentTeamPlayerMap,
  selectedDate,
}) => {

  const { currentUser } = useAuthContext();
  const { width, height } = useDebouncedWindowSize();
  const { openConfirmModal } = useOpenGlobalModal();

  const userConfig = useRecoilValue(userConfigState);
  const userSettings = useRecoilValue(userSettingsState);
  const tenantSettings = useRecoilValue(tenantSettingsState);
  const settingsInitialized = userSettings !== null && tenantSettings !== null;

  const players = useRecoilValue(playersState);
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const { setAndTrackSelectedPlayer } = useSetSelectedPlayer();

  const teams = useRecoilValue(teamsState);
  const playerTeamData = useRecoilValue(playerTeamDataSelector);

  const [isExpanding, setIsExpanding] = useState(false);

  const [isIconHighlighted, setIsIconHighlighted] = useState(false);

  const [teamData, setTeamData] = useState<InitialTeamDataPoint[]>([]);
  const [teamDataSimulationNodes, setTeamDataSimulationNodes] = useState<TeamDataPoint[] | undefined>(undefined);
  const lowestSafeMarginXToNodes = useMemo(() => {
    return teamDataSimulationNodes?.reduce((acc, node) => Math.min(acc, node.lowestSafeMarginXToNodes), 100);
  }, [teamDataSimulationNodes]);
  const [hasReTriggeredSimulation, setHasReTriggeredSimulation] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const teamIsAcademyTeam = (team as (Team | ArchivedTeam))?.isAcademyTeam;
  const playerSimpleTableType: PlayerSimpleTableType = teamIsAcademyTeam ? 'academyTeam' : teamId as PlayerSimpleTableType;

  const teamMenu = useMemo(() => {
    const menu = playerSimpleTableType === 'ownTeam'
      ? userSettings?.ownTeamMenu
      : playerSimpleTableType === 'ownTeamPlanner'
        ? userSettings?.ownTeamPlannerMenu
        : playerSimpleTableType === 'academyTeam'
          ? userSettings?.academyTeamMenu
          : userSettings?.teamMenu;
    return menu ?? {};
  }, [playerSimpleTableType, userSettings?.ownTeamMenu, userSettings?.ownTeamPlannerMenu, userSettings?.academyTeamMenu, userSettings?.teamMenu]);


  const onPlayerClick = (player: StringToAnyMap) => {
    if (handlePlayerClick) {
      handlePlayerClick(player);
      return;
    }

    setAndTrackSelectedPlayer({
      id: player.id,
      name: player.name,
      playerOverview: playerOverviews[player.id] ?? (
        additionalPlayerOverviews
          ? additionalPlayerOverviews[player.id]
          : undefined
      ),
    });
  };


  const handleAddPlayerIconClick = () => {
    if (!setIsLeftSectionExpanded || !setRightSectionExpansionType) return;

    setIsExpanding(true);
    setIsLeftSectionExpanded(!isLeftSectionExpanded);
    setRightSectionExpansionType(undefined);
    setTimeout(() => {
      setIsExpanding(false);
    }, 250);
  };


  const handleRightIconClick = (expansionType: 'history' | 'menu') => {
    if (!setIsLeftSectionExpanded || !setRightSectionExpansionType) return;

    const isDeexpansion = rightSectionExpansionType === expansionType;
    setRightSectionExpansionType(isDeexpansion ? undefined : expansionType);

    if (expansionType === 'history' && !isDeexpansion) {
      trackEvent('TeamHistoryViewed', {}, currentUser, 'user');
    }

    setIsExpanding(true);
    setIsLeftSectionExpanded(false);
    setTimeout(() => {
      setIsExpanding(false);
    }, 250);
  };


  const deleteOrRestoreArchivedTeamInternal = () => {
    if (!deleteOrRestoreArchivedTeam) return;

    deleteOrRestoreArchivedTeam();

    if (isArchivedTeamDeleted) {
      setIsIconHighlighted(true);
      setTimeout(() => {
        setIsIconHighlighted(false);
      }, 750);
    }
  };


  // this is where we compute the value to show for role index, based on the positionToRoleIndexConfig settings
  // there are 5 options:
  // no role configured for position -> show skill index
  // at least one of the roles configured is not ready -> show 'beingComputed'
  // aggregationRule === 'priority' -> show the first role index present
  // aggregationRule === 'max' -> show the max role index
  // aggregationRule === 'average' -> show the average of role indexes
  // aggregationRule === 'confidence' -> show the role index of the role with the highest confidence
  const getRoleIndexToShow = (
    player: StringToAnyMap,
    playerOverview: PlayerOverview,
    position: PlatformPosition,
  ) => {

    const roleIndexConfigOfPosition = tenantSettings?.positionToRoleIndexConfig?.[position];
    if (!roleIndexConfigOfPosition) {
      return player['skill_index'];
    }

    const roleIds = roleIndexConfigOfPosition.roleIds;
    const allRoleConfigsAreReady = roleIds.every((roleId) => {
      return !roleConfigIsBeingComputed(tenantSettings?.roleConfigs?.[roleId]);
    });

    if (!allRoleConfigsAreReady) {
      return 'beingComputed';
    }

    const aggregationRule = roleIndexConfigOfPosition.aggregationRule;
    if (aggregationRule === 'priority') {
      for (const roleId of roleIds) {
        const positionGroup = tenantSettings?.roleConfigs?.[roleId]?.positionGroup;
        if (positionGroup) {
          const roleIndex = playerOverview.position_group_stats?.[positionGroup]?.custom_indexes[roleId]?.index;
          if (roleIndex !== undefined && roleIndex !== null) {
            return roleIndex;
          }
        }

      }
    }

    else if (aggregationRule === 'max') {
      let maxValue = -1;
      for (const roleId of roleIds) {
        const positionGroup = tenantSettings?.roleConfigs?.[roleId]?.positionGroup;
        if (positionGroup) {
          const roleIndex = playerOverview.position_group_stats?.[positionGroup]?.custom_indexes[roleId]?.index;
          if (roleIndex !== undefined && roleIndex !== null) {
            maxValue = Math.max(maxValue, roleIndex);
          }
        }
      }
      if (maxValue !== -1) {
        return maxValue;
      }
    }

    else if (aggregationRule === 'average') {
      let sum = 0;
      let count = 0;
      for (const roleId of roleIds) {
        const positionGroup = tenantSettings?.roleConfigs?.[roleId]?.positionGroup;
        if (positionGroup) {
          const roleIndex = playerOverview.position_group_stats?.[positionGroup]?.custom_indexes[roleId]?.index;
          if (roleIndex !== undefined && roleIndex !== null) {
            sum += roleIndex;
            count++;
          }
        }
      }
      if (count > 0) {
        return sum / count;
      }
    }

    else if (aggregationRule === 'confidence') {
      let maxValue = -1;
      let maxConfidence = -1;
      for (const roleId of roleIds) {
        const positionGroup = tenantSettings?.roleConfigs?.[roleId]?.positionGroup;
        if (positionGroup) {

          const roleIndex = playerOverview.position_group_stats?.[positionGroup]?.custom_indexes[roleId]?.index;
          if (roleIndex !== undefined && roleIndex !== null) {
            const confidence = playerOverview.position_group_stats?.[positionGroup]?.custom_indexes[roleId]?.confidence;
            if (confidence && confidence > maxConfidence) {
              maxValue = roleIndex;
              maxConfidence = confidence;
            }
          }
        }
      }
      if (maxValue !== -1) {
        return maxValue;
      }
    }

    return undefined;
  };


  // Drag section for deleting players
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [playerWasRecentlyRemoved, setPlayerWasRecentlyRemoved] = useState(false);

  const showDeleteIcon = draggedPlayer && draggedPlayer['positionKey'] !== 'addPlayerTable' && draggedPlayer['positionKey'] !== 'addPlayerTableExistingPlayers';

  const handleRemovePlayerDrop = () => {

    if (draggedPlayer !== undefined && teamId && team && userConfig) {

      const playerId = getStrictPlayerId(draggedPlayer['id']);

      if (setDroppedPlayer) {
        setDroppedPlayer({ ...draggedPlayer, droppedPositionKey: 'delete' });
      }

      if (teamId === 'ownTeam') {
        if (!isNaN(Number(playerId)) && handleOpenAddClauseModal) {
          handleOpenAddClauseModal(Number(playerId), false);
        }

        removePlayerFromOwnTeam(playerId, players?.[playerId]?.isAcademyPlayer ?? false, userConfig.club, currentUser);
      }

      // if the player is no longer in any academy team, we open a modal to ask the user if they want to remove the player from the academy
      else if (teamIsAcademyTeam) {
        const currentAcademyTeams = teams && playerTeamData && playerTeamData[playerId]
          ? Object.keys(playerTeamData[playerId]).filter((teamId) => teams[teamId]?.isAcademyTeam)
          : [];

        const playerIsInOwnTeam = playerTeamData?.[playerId]?.['ownTeam'] !== undefined;

        if (currentAcademyTeams.length <= 1) {
          setTimeout(() => {
            openConfirmModal(
              () => removePlayerFromAcademy(playerId, playerIsInOwnTeam, userConfig.club, currentUser),
              'removePlayerFromAcademy?',
              'removePlayerFromAcademy?Info',
            );
          }, 250);
        }
      }


      removePlayerFromTeam(
        playerId,
        teamId,
        team as Team,
        draggedPlayer['positionKey'],
        userConfig.email,
        userConfig.club,
        currentUser,
        'dragged'
      );
    }

    setPlayerWasRecentlyRemoved(true);
    handleDragLeave();

    if (setDraggedPlayer) {
      setDraggedPlayer(undefined);
    }

    setTimeout(() => {
      setPlayerWasRecentlyRemoved(false);
    }, 250);

  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDragEnter = () => {
    setIsDraggedOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggedOver(false);
  };


  const getBoxShadow = () => {
    if (isDraggedOver) {
      return '0px 0px 0px 3px #ff2424';
    }
    return undefined;
  };


  // prepare player table for a given position
  const getTeamDataPointPlayers = (playerList: PlayerEntry[] | undefined, position: PlatformPosition) => {

    if (!playerList) return [];

    const newPlayerList: StringToAnyMap[] = [];

    playerList.forEach(playerEntry => {

      const player: StringToAnyMap = { ...playerEntry };

      const playerDocument = (players && players[player.id]) ?? {};

      const shouldHidePlayerDueToStartingEleven =
        teamId === 'ownTeam' &&
        showFirstEleven &&
        !playerDocument.isStartingEleven;

      const shouldHidePlayerDueToAvailability = userSettings && (
        (
          teamId === 'ownTeam' && userSettings.ownTeamMenu && userSettings.ownTeamMenu.hideUnavailablePlayers &&
          (playerDocument.role === 'longTermInjured' || playerDocument.role === 'loanedOut')
        ) ||
        (
          teamId === 'ownTeamPlanner' && userSettings.ownTeamPlannerMenu && userSettings.ownTeamPlannerMenu.hideUnavailablePlayers && currentTeamPlayerMap &&
          (currentTeamPlayerMap[player.id].role === 'longTermInjured' || currentTeamPlayerMap[player.id].role === 'loanedOut')
        ) ||
        (
          teamIsAcademyTeam && userSettings.academyTeamMenu && userSettings.academyTeamMenu.hideUnavailablePlayers &&
          playerDocument.academyRole === 'transferTarget'
        )
      );

      if (player && !(shouldHidePlayerDueToStartingEleven || shouldHidePlayerDueToAvailability)) {

        const playerOverview = playerOverviews[player.id] ?? additionalPlayerOverviews?.[player.id];

        if (playerOverview) {

          player['image_url'] = playerOverview.image_url;

          player['birth_date'] = playerOverview.birth_date;
          player['country_code'] = playerOverview.country_code;
          player['contract_expiration'] = playerOverview.contract_expiration;

          player['position_info'] = playerOverview.position_info;
          player['club_logo_url'] = playerOverview.club?.logo_url;

          player['form_status'] = playerOverview.form_status;
          player['availability_status'] = playerOverview.availability_status;

          player['skill_index'] = playerOverview.position_group_stats?.OVERALL?.skill_index?.index;
          player['role_index'] = getRoleIndexToShow(player, playerOverview, position);
          player['physical_index'] = playerOverview.position_group_stats?.OVERALL?.physical_index?.index;
        }


        if (player.id in players) {
          Object.entries(players[player.id]).forEach(([property, value]) => {
            if (value !== undefined && value !== null) {
              player[property] = value;
            }
          });
        }

        if (currentTeamPlayerMap && player.id in currentTeamPlayerMap) {
          player['contract_expiration'] = currentTeamPlayerMap[player.id].contract_expiration;
          player['role'] = currentTeamPlayerMap[player.id].role;
          player['locality'] = currentTeamPlayerMap[player.id].locality;
        }

        const dateString = selectedDate?.toISOString().split('T')[0];
        player['age'] = getAge(player.birth_date, dateString);
        player['ageCategory'] = getAgeCategory(player.birth_date, dateString);
        player['monthsLeftOfContract'] = getMonthsLeftOfContract(player.contract_expiration, dateString);
        player['contractCategory'] = getContractCategory(player.contract_expiration, dateString);

        newPlayerList.push(player);
      }
    });

    const sortBy = teamMenu.orderBy ?? defaultOrderBy;
    newPlayerList.sort((a: StringToAnyMap, b: StringToAnyMap) => {
      if (!sortBy) return 0;

      if (!a[sortBy]) return 1;
      if (!b[sortBy]) return -1;

      const oneValue = sortBy === 'age' || sortBy === 'ageCategory' ? -1 : 1;
      const minusOneValue = sortBy === 'age' || sortBy === 'ageCategory' ? 1 : -1;

      const aValue = (sortBy === 'role' || sortBy === 'academyRole')
        ? (isValidPlayerRole(a[sortBy]) ? playerRoleToSortOrder[a[sortBy]] : 0)
        : sortBy === 'locality'
          ? (isValidPlayerLocality(a[sortBy]) ? playerLocalityToSortOrder[a[sortBy]] : 0)
          : a[sortBy];

      const bValue = (sortBy === 'role' || sortBy === 'academyRole')
        ? (isValidPlayerRole(b[sortBy]) ? playerRoleToSortOrder[b[sortBy]] : 0)
        : sortBy === 'locality'
          ? (isValidPlayerLocality(b[sortBy]) ? playerLocalityToSortOrder[b[sortBy]] : 0)
          : b[sortBy];

      return aValue > bValue ? minusOneValue : oneValue;
    });

    return newPlayerList;
  };


  useEffect(() => {
    if (teamId && team && tenantSettings) {
      const formationConfig = tenantSettings?.formationConfigs?.[team.formation] ?? defaultFormationConfigMap[team.formation];
      const teamData: InitialTeamDataPoint[] = [];

      // we want to ensure that the team is aligned with the formation config - this should always be the case
      const alignedTeam = getAlignedTeam(teamId, team, formationConfig, currentUser);

      // if the formation has a typical backline with goalkeeper, but the team does not have a goalkeeper, we let the other positions cover more of the field
      const teamHasTypicalBackline = formationConfigHasTypicalBackline(formationConfig);
      const teamHasGoalkeeper = alignedTeam.players?.GK && alignedTeam.players?.GK.length > 0;

      Object.entries(formationConfig).forEach(([position, positionCoordinates]) => {
        const playerList = getTeamDataPointPlayers(alignedTeam.players[position], position as PlatformPosition);

        const scaleOutfieldPositions = teamHasTypicalBackline && !teamHasGoalkeeper && position !== 'GK';
        const gridPosition = getGridPosition(positionCoordinates.x, positionCoordinates.y, scaleOutfieldPositions);

        teamData.push({
          position: position as PlatformPosition,
          left: gridPosition.left,
          top: gridPosition.top,
          xCoordinate: positionCoordinates.x,
          yCoordinate: positionCoordinates.y,
          playerList,
        });
      });

      setTeamData(teamData);
    }
  },
  // eslint-disable-next-line
  [
    team,
    players,
    playerOverviews,
    tenantSettings,
    additionalPlayerOverviews,
    teamMenu.orderBy,
    teamMenu.hideUnavailablePlayers,
    showFirstEleven,
    benchPlayers
  ]);


  const tableRowHeight = teamId === 'roleConfigs'
    ? roleConfigTableRowHeight
    : getPlayerSimpleTableRowHeight(playerSimpleTableType, isSmallTeamView);

  const tableWidth = teamId === 'roleConfigs'
    ? roleConfigTableWidth
    : getEmptyPlayerSimpleTableWidth(playerSimpleTableType, teamMenu, isSmallTeamView, defaultToShortNames);


  useEffect(() => {
    if (!teamData.length || !containerRef.current || !settingsInitialized) return;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    const updatedNodes = resolveTableCollisions(
      teamData,
      tableWidth,
      tableRowHeight,
      containerWidth,
      containerHeight,
      teamId === 'roleConfigs',
    );

    setTeamDataSimulationNodes(updatedNodes);
  }, [teamId, teamData, tableWidth, tableRowHeight, width, height, settingsInitialized]);


  // separate effect for expansion
  // we want to re-calculate nodes if current nodes are close in x direction and we are expanding, or we have re-triggered the simulation and are de-expanding
  useEffect(() => {
    if (!teamData.length || !containerRef.current || !userSettings || isExpanding || lowestSafeMarginXToNodes === undefined) return;

    const isExpanded = isLeftSectionExpanded || rightSectionExpansionType !== undefined;
    const shouldReTriggerSimulation = (isExpanded && lowestSafeMarginXToNodes < 30) || hasReTriggeredSimulation;

    if (shouldReTriggerSimulation) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;

      const updatedNodes = resolveTableCollisions(
        teamData,
        tableWidth,
        tableRowHeight,
        containerWidth,
        containerHeight,
        teamId === 'roleConfigs',
      );
      setTeamDataSimulationNodes(updatedNodes);
      setHasReTriggeredSimulation(true);
    }

  }, [isExpanding]); // eslint-disable-line react-hooks/exhaustive-deps


  const shouldShowTeamViewIcons = teamId !== 'ownTeamPlanner' && teamId !== 'archivedTeam' && teamId !== 'roleConfigs';

  const tableTransitionToUse = tableTransition ?? 150;


  return (
    <div className='team-view-container'>

      <FootballFieldSvg />

      <div
        key={teamId + ((team as ArchivedTeam)?.id ?? '-') + (showFirstEleven ? 'showFirstEleven' : '-')}
        ref={containerRef}
        className='team-view-team-data-points-section fast-fade-in'
      >

        {teamId && teamDataSimulationNodes && teamDataSimulationNodes.map((teamDataPoint, index) => {

          return (
            <div
              key={index + teamId + ((team as ArchivedTeam)?.id ?? '-') + (showFirstEleven ? 'showFirstEleven' : '-')}
              className='team-view-team-data-point-container'
              style={{
                left: `calc(${teamDataPoint.left}% - ${teamDataPoint.width / 2}px)`,
                top: `calc(${teamDataPoint.top}% - ${teamDataPoint.height / 2}px)`,
                transition: 'left ' + tableTransitionToUse + 'ms ease-out, top ' + tableTransitionToUse + 'ms ease-out',
              }}
            >
              {teamId !== 'roleConfigs' && (
                <div
                  key={index + teamId + ((team as ArchivedTeam)?.id ?? '-') + (showFirstEleven ? 'showFirstEleven' : '-')}
                  style={{
                    width: getEmptyPlayerSimpleTableWidth(playerSimpleTableType, teamMenu, isSmallTeamView, defaultToShortNames),
                    transition: 'width ' + tableTransitionToUse + 'ms ease-out',
                  }}
                >
                  <PlayerSimpleTable
                    tableData={teamDataPoint.playerList}
                    onPlayerClick={onPlayerClick}
                    tableType={playerSimpleTableType}
                    positionKey={teamDataPoint.position}
                    draggedPlayer={draggedPlayer}
                    setDraggedPlayer={hasOnlyReadAccess ? undefined : setDraggedPlayer}
                    handlePlayerDrop={hasOnlyReadAccess ? undefined : handlePlayerDrop}
                    tableHoverDisabled={tableHoverDisabled}
                    droppedPlayer={droppedPlayer}
                    maxHeight={teamDataPoint.height < teamDataPoint.desiredHeight ? teamDataPoint.height + 'px' : undefined}
                    highlightedPlayerId={highlightedPlayerId}

                    tableTransition={tableTransitionToUse}
                    isSmallTeamView={isSmallTeamView}
                    defaultOrderBy={defaultOrderBy}
                    defaultToShortNames={defaultToShortNames}
                  />
                </div>
              )}

              {teamId === 'roleConfigs' && (
                <div
                  className='team-view-player-section fast-fade-in'
                  style={{ width: roleConfigTableWidth }}
                >
                  <RoleConfigTable
                    tableData={teamDataPoint.playerList}
                    onRoleClick={onPlayerClick}
                    position={teamDataPoint.position}
                    draggedRole={draggedPlayer}
                    setDraggedRole={hasOnlyReadAccess ? undefined : setDraggedPlayer}
                    handleRoleDrop={hasOnlyReadAccess ? undefined : handlePlayerDrop}
                    tableHoverDisabled={tableHoverDisabled}
                    droppedRole={droppedPlayer}
                    confirmNewAggregationRule={hasOnlyReadAccess ? undefined : confirmNewAggregationRule}
                  />
                </div>
              )}
            </div>
          );
        })}

      </div>

      {shouldShowTeamViewIcons && showDeleteIcon && !hasOnlyReadAccess && (
        <div
          className='team-view-delete-player-icon-container-overlay'
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleRemovePlayerDrop}
        />
      )}

      {shouldShowTeamViewIcons && !hasOnlyReadAccess && (
        <div
          style={{ boxShadow: getBoxShadow(), opacity: playerWasRecentlyRemoved ? 0 : 1 }}
          className={'team-view-circular-button team-view-add-player-button' + (showDeleteIcon ? ' team-view-delete-player-icon-container' : '')}
          onClick={() => !showDeleteIcon ? handleAddPlayerIconClick() : null}
        >
          {!showDeleteIcon && !isLeftSectionExpanded && <PersonAddAlt1Icon style={{ fontSize: 24, marginLeft: 2, marginTop: -2 }} />}
          {!showDeleteIcon && isLeftSectionExpanded && <KeyboardArrowLeftIcon style={{ fontSize: 24, marginLeft: -1 }} />}
          {showDeleteIcon && <DeleteForeverIcon style={{ fontSize: 28 }} />}

          {!isLeftSectionExpanded && !isExpanding && (
            <div className='team-view-circular-button-hover-text-container'>
              <div className='team-view-circular-button-hover-text'>
                {translate('addPlayer', userConfig?.language)}
              </div>
            </div>
          )}
        </div>
      )}

      {shouldShowTeamViewIcons && (
        <div
          className='team-view-circular-button team-view-history-button'
          onClick={() => handleRightIconClick('history')}
        >
          {rightSectionExpansionType !== 'history' && <LibraryBooksIcon style={{ fontSize: 22 }} />}
          {rightSectionExpansionType === 'history' && <KeyboardArrowRightIcon style={{ fontSize: 24 }} />}
          {rightSectionExpansionType !== 'history' && !isExpanding && (
            <div className='team-view-circular-button-hover-text-container'>
              <div className='team-view-circular-button-hover-text'>
                {translate('log', userConfig?.language)}
              </div>
            </div>
          )}
        </div>
      )}

      {shouldShowTeamViewIcons && (
        <div
          className='team-view-circular-button team-view-menu-button'
          onClick={() => handleRightIconClick('menu')}
        >
          {rightSectionExpansionType !== 'menu' && <MenuIcon style={{ fontSize: 22 }} />}
          {rightSectionExpansionType === 'menu' && <KeyboardArrowRightIcon style={{ fontSize: 24 }} />}
          {rightSectionExpansionType !== 'menu' && !isExpanding && (
            <div className='team-view-circular-button-hover-text-container'>
              <div className='team-view-circular-button-hover-text'>
                {translate('teamMenu', userConfig?.language)}
              </div>
            </div>
          )}
        </div>
      )}


      {teamId === 'archivedTeam' && closeArchivedTeam && (
        <div
          className='team-view-circular-button team-view-close-button'
          onClick={() => closeArchivedTeam()}>
          {<CloseIcon style={{ fontSize: 22 }} />}
        </div>
      )}

      {teamId === 'archivedTeam' && deleteOrRestoreArchivedTeam && (
        <div
          className={'team-view-circular-button team-view-delete-or-restore-button' + (isIconHighlighted ? ' team-view-circular-button-highlighted' : '')}
          onClick={() => deleteOrRestoreArchivedTeamInternal()}
        >
          {isArchivedTeamDeleted && <RestoreIcon style={{ fontSize: 22 }} />}
          {!isArchivedTeamDeleted && <DeleteOutlineIcon style={{ fontSize: 22 }} />}
          {!isIconHighlighted && (
            <div className='team-view-circular-button-hover-text-container'>
              <div className='team-view-circular-button-hover-text'>
                {translate(isArchivedTeamDeleted ? 'restore' : 'delete', userConfig?.language)}
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};
